import * as React from 'react';
import {createViewConfig, view, media} from '@sail/engine';
import {css, tokens} from '@sail/ui';
import {useId} from '@sail/react-aria';

import {
  TestimonialCard,
  TestimonialCardConfig,
} from 'mkt-ssr/components/sail/TestimonialCard/TestimonialCard';
import type {TestimonialCardProps} from 'mkt-ssr/components/sail/TestimonialCard/TestimonialCard';
import {mixColors, clamp} from '@sail/helm';
import {styles} from 'mkt-ssr/components/sail/TestimonialCarousel/TestimonialCarouselStyles';
import {CustomerLogo} from 'mkt-ssr/components/sail/CustomerLogo/CustomerLogo';
import {navItemAriaLabel} from 'mkt-ssr/components/sail/TestimonialCarousel/TestimonialCarousel.messages';
import {useIntl} from '@sail/intl';

export type TestimonialCarouselProps = {
  /**
   * The data used to render the testimonial cards in the carousel.
   */
  data: TestimonialCardProps[];
};

export const TestimonialCarouselConfig = createViewConfig({
  props: {} as TestimonialCarouselProps,
  name: 'TestimonialCarousel',
});

interface Opacities {
  [key: number]: number;
}

// TODO: Remove this when we refactor UserLogo
const globalNavLogoStyles = css.global({
  '.navLogo svg': {
    maxWidth: '100%',
  },
});

/**
 * A component for displaying the testimonial from a Stripe user/customer.
 */
export const TestimonialCarousel = TestimonialCarouselConfig.createView(
  (props) => {
    const {data, ...rest} = props;
    const backgroundRefs = React.useRef<React.RefObject<HTMLDivElement>[]>(
      data.map(() => React.createRef<HTMLDivElement>()),
    );
    const {formatMessage} = useIntl();
    const containerEl = React.useRef<HTMLDivElement>(null);
    const trackEl = React.useRef<HTMLDivElement>(null);
    const [lastPageIndex, setLastPageIndex] = React.useState(0);
    const [activeTestimonialCardIndex, setActiveTestimonialCardIndex] =
      React.useState(0);
    const [colors, setColors] = React.useState<string[]>([]);
    const [currentBgColor, setCurrentBgColor] = React.useState('');
    const [testimonialCardOpacities, setTestimonialCardOpacities] =
      React.useState<Opacities>({});

    const carouselStyles = css({
      position: 'relative',
      stack: 'x',
      boxShadow: 'xlarge',
      backgroundColor: currentBgColor || tokens.color.neutral[900],
      borderRadius: 'medium',
      overflow: 'hidden',
    });

    // TODO: revert this when backgroundImage image hydration warning is resolved
    React.useEffect(() => {
      data.forEach((testimonialCard, index) => {
        const ref = backgroundRefs.current[index];
        if (ref && ref.current) {
          ref.current.style.setProperty(
            `--background`,
            `url(${testimonialCard.backgroundImage}?q=80&w=1600&h=900&fit=fill&f=faces&fm=webp)`,
          );
        }
      });
    }, [data]);

    const renderBackgroundStyles = () => {
      return (
        <view.div
          uses={[styles.backgroundsStyles, styles.backgroundsUnprocessedStyles]}
        >
          {data.map((_, index: number) => {
            const uid = useId();
            return (
              <view.div
                key={`background-${uid}`}
                className="TestimonialCarousel__background"
                ref={backgroundRefs.current[index]}
                css={{
                  opacity: testimonialCardOpacities[index],
                  position: 'absolute',
                  top: 'space.0',
                  left: 'space.0',
                  width: 'fill',
                  height: 'fill',
                  pointerEvents: 'none',
                  backgroundImage: `var(--background)`,
                  backgroundSize: '100% auto',
                  backgroundPosition: 'top center',
                  backgroundRepeat: 'no-repeat',
                  [media.up(tokens.viewport.medium)]: {
                    backgroundPosition: '130% 50%',
                    backgroundSize: 'auto 100%',
                  },
                  [media.up(tokens.viewport.large)]: {
                    backgroundPosition: '100% 50%',
                  },
                }}
              />
            );
          })}
        </view.div>
      );
    };

    const renderTestimonialCards = () => {
      return data.map((testimonialCard, index) => {
        const uid = useId();

        return (
          <TestimonialCard
            key={`card-${uid}`}
            {...testimonialCard}
            css={{opacity: testimonialCardOpacities[index]}}
          />
        );
      });
    };

    const renderIndicatorItems = () => {
      return data.map((_, index) => {
        const uid = useId();
        return (
          <view.li key={`indicator-${uid}`}>
            <view.button
              uses={[
                styles.indicatorStyles,
                activeTestimonialCardIndex === index &&
                  styles.activeIndicatorStyles,
              ]}
              data-js-target-list="TestimonialCarousel.indicators"
              aria-label={formatMessage(navItemAriaLabel)}
              onClick={() =>
                trackEl.current !== null &&
                trackEl.current.scrollTo({
                  left: index === 0 ? 0 : trackEl.current.offsetWidth * index,
                  behavior: 'smooth',
                })
              }
            />
          </view.li>
        );
      });
    };

    const renderNavItems = () => {
      return data.map((testimonialCard, index) => {
        const uid = useId();
        return (
          <view.li key={`nav-${uid}`}>
            <view.button
              uses={[
                styles.navLogoButtonStyles,
                styles.navLogoButtonUnprocessedStyles,
                globalNavLogoStyles,
              ]}
              className="navLogo"
              type="button"
              aria-label={formatMessage(navItemAriaLabel)}
              onClick={() =>
                trackEl.current !== null &&
                trackEl.current.scrollTo({
                  left: index === 0 ? 0 : trackEl.current.offsetWidth * index,
                  behavior: 'smooth',
                })
              }
            >
              <CustomerLogo
                colorWay={
                  activeTestimonialCardIndex === index ? 'fullColor' : 'flat'
                }
                logo={testimonialCard.logo}
                className="TestimonialCustomLogo"
                uses={[styles.navLogoStyles]}
              />
            </view.button>
          </view.li>
        );
      });
    };

    const scrollProgress = () => {
      if (trackEl.current && containerEl.current) {
        const {scrollLeft} = trackEl.current;
        const {width} = containerEl.current.getBoundingClientRect();

        const currentPage = Math.floor((scrollLeft - width / 2) / width) + 1;
        const offset = scrollLeft - currentPage * width;
        const nextPage = offset > 0 ? currentPage + 1 : currentPage - 1;

        const nextProgress = Math.abs(offset) / width;
        const currentProgress = 1 - nextProgress;

        return [
          {page: currentPage, progress: currentProgress},
          {page: nextPage, progress: nextProgress},
        ];
      }
      return [
        {page: 0, progress: 0},
        {page: 0, progress: 0},
      ];
    };

    const handleScroll = () => {
      const [current, next] = scrollProgress();

      if (current.page !== lastPageIndex) {
        setLastPageIndex(current.page);
        setActiveTestimonialCardIndex(current.page);
      }

      // Update bg color
      const currentColor = clamp(current.page, 0, data.length - 1);
      const nextColor = clamp(next.page, 0, data.length - 1);
      const mixedColor = mixColors(
        colors[currentColor],
        colors[nextColor],
        current.progress,
      );
      setCurrentBgColor(mixedColor);

      // set the opacity of the current and next images
      setTestimonialCardOpacities({
        ...testimonialCardOpacities,
        [current.page]: current.progress,
      });
      setTestimonialCardOpacities({
        ...testimonialCardOpacities,
        [next.page]: next.progress,
      });

      // This was broken at some point but now appears to work fine, however
      // if you ever see aggressive stuttering when overflowing the carousel
      // just disable these next lines
      if (current.page >= 0 && current.page <= data.length - 1) {
        setTestimonialCardOpacities((cardOpacities) => {
          return {
            ...cardOpacities,
            [current.page]: current.progress,
          };
        });
      }
      if (next.page >= 0 && next.page <= data.length - 1) {
        setTestimonialCardOpacities((cardOpacities) => {
          return {
            ...cardOpacities,
            [next.page]: next.progress,
          };
        });
      }
    };

    React.useEffect(() => {
      setColors(
        data.map((testimonialCard) => {
          return testimonialCard.backgroundColor;
        }),
      );

      setTestimonialCardOpacities(
        data.map((_testimonialCard, index) => (index === 0 ? 1 : 0)),
      );

      setCurrentBgColor(data[0].backgroundColor);
    }, [data]);

    return (
      <view.div {...rest} ref={containerEl}>
        <view.div uses={[carouselStyles]}>
          {renderBackgroundStyles()}
          <view.div
            uses={[styles.trackStyles, styles.trackUnprocessedStyles]}
            onScroll={handleScroll}
            ref={trackEl}
          >
            {renderTestimonialCards()}
          </view.div>
          <view.nav uses={[styles.indicatorsStyles]}>
            <view.ul uses={[styles.indicatorListStyles]}>
              {renderIndicatorItems()}
            </view.ul>
          </view.nav>
        </view.div>
        <view.nav uses={[styles.navStyles]}>
          <view.ul
            uses={[styles.navListStyles, styles.navListUnprocessedStyles]}
          >
            {renderNavItems()}
          </view.ul>
        </view.nav>
      </view.div>
    );
  },
  {
    uses: [
      TestimonialCardConfig.customize({
        css: {
          backgroundColor: 'transparent',
          backgroundImage: 'none',
          minWidth: '100%',
          minHeight: '300px',
        },
        uses: [css({scrollSnapAlign: 'center'})],
      }),
    ],
  },
);
